import * as twilioAction
  from "../../../shared/modules/twilio/actions/twilioAction";
import * as rocketChatAction
  from "../../../shared/modules/rocketChat/actions/rocketChatAction";
import {VIDEO_CALL_WAIT} from "../constants";
import {
  VIDEO_ACCEPT,
  VIDEO_CANCELLED,
  VIDEO_DECLINE,
  VIDEO_DECLINED,
  VIDEO_INITIATE,
  VIDEO_JOIN,
  VIDEO_MISS,
  VIDEO_START,
  VIDEO_STOP,
  VIDEO_STOPPED
} from "../constants/actionTypes";
import * as notificationAction
  from "../../../shared/modules/notification/actions/notificationsAction";
import {
  NOTIFICATION_ERROR,
  NOTIFICATION_SUCCESS,
  NOTIFICATION_WARNING
} from "../../../shared/modules/notification/constants";
import {
  TW_ACTION_SEPARATOR,
  TW_ERROR_OPEN
} from "../../../shared/modules/twilio/constants";
import {isCordova} from "../../../app";
import {PLATFORM_IOS} from "../../../shared/constants";
import {
  RC_USER_PREFIX_FP,
  RC_USER_PREFIX_T
} from "../../../shared/modules/rocketChat/constants";
import {getSenior} from "../../members/actions/seniorsAction";
import {videoStarted} from "../../../shared/modules/notification/actions/localAction";

const callbacks = {};

/**
 * Gets video room identification from notificationAction without action
 * @param {object} notification notificationAction
 * @returns {string} room identification
 * @private
 */
export function getRoom(notification) {
  return notification.content.split(TW_ACTION_SEPARATOR)[0]
}

/**
 * Removes room
 * @param {object} video video object
 * @returns {function(dispatch)} promised thunk to be dispatched
 * @private
 */
export function removeRoom(video) {
  return (dispatch) => {
    clearTimeout(callbacks[video.room]);
    return dispatch(twilioAction.removeRoom())
      .catch(() => {
        console.log(`Failed to stop a call`);
      })
  }
}

/**
 * Stops a video call
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function stop() {
  return (dispatch, getState) => {
    const {video} = getState();

    if (video.isEstablished) {
      return dispatch(removeRoom(video))
        .then(() => {
          dispatch({type: VIDEO_STOP});
        })
        .then(() => {
          const {user, room} = video;

          if (video.isJoined) {
            dispatch(rocketChatAction.stopCall(user, room));
          } else {
            dispatch(rocketChatAction.cancelCall(user, room));
          }
        });
    }
    if (isCordova() && cordova.platformId === PLATFORM_IOS) {
      const {user, room} = video;

      dispatch(rocketChatAction.cancelCall(user, room));
    }
    return Promise.resolve();
  };
}

/**
 * Someone joined a video call
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function join() {
  return (dispatch, getState) => {
    const {video} = getState();

    if (!video.isJoined) {
      videoStarted(
        dispatch(getSenior(rocketChatAction.getSeniorId(video.user))),
        () => {
          dispatch(stop())
        }
      );
      dispatch({type: VIDEO_JOIN});
    }
  };
}

/**
 * Initialization of the room
 * @param {object} room - room that was connected to
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function initializeRoom(room) {
  return (dispatch) => {
    if (!(isCordova() && cordova.platformId === PLATFORM_IOS)) {
      dispatch(twilioAction.addRoom(room));
    }

    return room;
  }
}

/**
 * Processed when a call was stopped
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function stopped() {
  return (dispatch, getState) => {
    const {video} = getState();

    if (video.isEstablished) {
      dispatch(removeRoom(video))
        .then(() => {
          dispatch({type: VIDEO_STOPPED});
        });
    }
  }
}

/**
 * Processed when a call was declined
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function declined() {
  return (dispatch, getState) => {
    const {video} = getState();

    dispatch(removeRoom(video))
      .then(() => {
        const user = dispatch(getSenior(rocketChatAction.getSeniorId(getState().video.user)));

        dispatch(
          notificationAction.addNotification(
            __("%user% has rejected your call")
              .replace('%user%', `${user.firstname} ${user.lastname}`),
            NOTIFICATION_WARNING
          )
        );
      })
      .then(() => {
        dispatch({type: VIDEO_DECLINED});
      })
  };
}


/**
 * Processed when a call was cancelled
 * @param {string} rid RC room identification
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function cancelled(rid) {
  return (dispatch, getState) => {
    getState().rocketChat.notifications.forEach((notification, index) => {
      if (notification.content.startsWith(RC_USER_PREFIX_T) &&
          notification.rid === rid
      ) {
        dispatch(rocketChatAction.checkNotification(index))
          .then(() => {
            dispatch({type: VIDEO_CANCELLED});
          });
      }
    });
  };
}

/**
 * Declines pending video call
 * @param {object} notification message
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function decline(notification) {
  return (dispatch, getState) => {
      const index = getState().rocketChat.notifications.indexOf(notification);
      const roomId = getRoom(dispatch(rocketChatAction.getNotification(index)));
      const user = dispatch(getSenior(rocketChatAction.getSeniorId(roomId)));

      dispatch(rocketChatAction.declineCall(user.username, roomId))
      .then(() => {
        dispatch(rocketChatAction.checkNotification(index));
      })
      .then(() => {
        dispatch({type: VIDEO_DECLINE});
      });
  };
}

/**
 * Call
 * @param {string} user - username
 * @param {string} roomId - room identification
 * @param {object} userModel - user model
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function call(user, roomId, userModel) {
  return (dispatch, getState) => {
    if (isCordova() && cordova.platformId === PLATFORM_IOS) {
      dispatch(rocketChatAction.inviteCall(user, roomId));
      dispatch(twilioAction.connectVideo(roomId));
    } else {
      dispatch(twilioAction.connectVideo(roomId))
        .then((room) => {
          dispatch(rocketChatAction.inviteCall(user, room.name));
          return room;
        })
        .then((room) => {
          dispatch(
            {
              type: VIDEO_START,
              room: room.name,
              user: user
            }
          );
          callbacks[room.name] = setTimeout(() => {
            if (room.participants &&
              !room.participants.size &&
              getState().video.isEstablished
            ) {
              dispatch(stop())
                .then(() => {
                  dispatch(
                    notificationAction.addNotification(
                      __("%user% is currently not available.")
                        .replace('%user%', `${userModel.firstname} ${userModel.lastname}`),
                      NOTIFICATION_WARNING
                    )
                  );
                });
            }
          }, VIDEO_CALL_WAIT);

          return dispatch(initializeRoom(room));
        })
        .catch((error) => {
          dispatch(stop());
          switch (error.message) {
            case TW_ERROR_OPEN:
              dispatch(
                notificationAction.addNotification(
                  __("You are offline and can not make calls."),
                  NOTIFICATION_ERROR
                )
              );
              break;
            default:
              dispatch(
                notificationAction.addNotification(
                  __("Please check your webcam settings."),
                  NOTIFICATION_ERROR
                )
              );
          }
        });
    }
  }
}

/**
 * Starts a video call
 * @param {string} user - username
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function start(user) {
  return (dispatch, getState) => {
    const roomId = `${getState().user.cust_id}_${Date.now()}`;

    const userModel = getState().seniors.find((element) => {
      return element.prof_id.toString() === user.split("_").pop();
    });

    dispatch(
      notificationAction.addNotification(
        __("%user% has called")
          .replace('%user%', `${userModel.firstname} ${userModel.lastname}`),
        NOTIFICATION_SUCCESS
      )
    );
    dispatch(call(user, RC_USER_PREFIX_FP + roomId, userModel));
  };
}

/**
 * Accepts a call
 * @param {object} notification - message
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function accept(notification) {
  return (dispatch, getState) => {
    const index = getState().rocketChat.notifications.indexOf(notification);

    return dispatch(rocketChatAction.checkNotification(index))
      .then(() => {
        return dispatch(twilioAction.connectVideo(notification.content))
      })
      .then(
        (room) => {
          if (!(isCordova() && cordova.platformId === PLATFORM_IOS)) {
            dispatch(
              {
                type: VIDEO_ACCEPT,
                user: notification.sender.username,
                room: room.name
              }
            );
            return dispatch(initializeRoom(room));
          }
        },
        () => {
          throw new Error('Please check your video device');
        }
      );
  }
}

/**
 * Initiate a call
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function initiate() {
  return (dispatch) => {
    return dispatch(
      {type: VIDEO_INITIATE}
    );
  };
}

/**
 * Misses a call
 * @param {string} user - userName
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function miss(user) {
  return (dispatch) => {
    return dispatch(
      {
        type: VIDEO_MISS,
        user: user
      }
    );
  };
}
